// THE TWO PARTNER CONTROLS on the Contractor Hours & Cost toolbar.
//
// Owner, 2026-10-22: "why in the contractor filter, i don't see all
// partners/disciplines to filter issues assigned to one of their team
// members" - then "make it a second partner team filter, and show all
// partners".
//
// WHY THE CONTRACTOR LIST WAS SHORT. It was built from the report's own
// output: data.contractors is the companies that have LINES, so a partner
// with no hours inside the current project, dates and saved filters was not
// in the dropdown at all. Narrow the dates and partners disappear from the
// filter - which reads as a broken list, not as an empty report. The roster
// is the right source for "who could I filter by", and the report is the
// right source for "who is in this report"; the list is the union, and a
// company with nothing here SAYS SO rather than being dropped.
//
// In its own file, not inline on the page: ContractorHoursPage is already
// past the hooks lint's path budget, where extra branches make it report a
// hook as conditionally called at an unrelated earlier line.
import React from "react";
import { Select, Tooltip } from "antd";
import { tradesText } from "components/Partners/partnerTrades";
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import { matchOption } from "components/SearchSelect/searchSelect";

/**
 * Every company that can be filtered on, newest knowledge first:
 * the project roster, plus any company in the report that the roster does
 * not list (a company that billed hours and has since been taken off the
 * roster still has to be selectable, or its lines become unreachable).
 *
 * `roster` is the PeoplePicker roster; `reported` is report.contractors.
 * Pure, so the "no hours here" marking is tested without the page.
 *
 * @returns {Array<{value, label, inReport, isOwner}>}
 */
export function partnerOptions(roster, reported, { includeOwner = false } = {}) {
  const withLines = new Map();
  for (const c of reported || []) {
    if (c && c.companyId) withLines.set(String(c.companyId), c.companyName || String(c.companyId));
  }
  const out = [];
  const seen = new Set();
  for (const g of roster || []) {
    const id = String((g && g.companyId) || "");
    if (!id || seen.has(id)) continue;
    const isOwner = g.role === "owner";
    if (isOwner && !includeOwner) continue;
    seen.add(id);
    out.push({
      value: id,
      label: withLines.get(id) || g.companyName || id,
      inReport: withLines.has(id),
      isOwner,
    });
  }
  // In the report but not on the roster - kept, because its lines are real.
  for (const [id, name] of withLines) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ value: id, label: name, inReport: true, isOwner: false, offRoster: true });
  }
  return out.sort((a, b) => String(a.label).localeCompare(String(b.label)));
}

/**
 * The options as antd wants them: the trades underneath, searchable by name
 * and trade, the closed box keeping the plain name - and a quiet note on a
 * company that has nothing in the report as it stands, so an empty result
 * reads as "nothing here for them" rather than as a filter that is broken.
 */
export function decorate(options, ix, { markEmpty = true } = {}) {
  return (options || []).map((o) => {
    const t = (ix && ix.tradesOf(o.value)) || [];
    const name = typeof o.label === "string" ? o.label : String(o.value);
    const note = markEmpty && o.inReport === false ? "no hours in this report" : "";
    return {
      ...o,
      text: name,
      search: `${name} ${tradesText(t)}`,
      label: (
        <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          {t.length ? <PartnerWithTrades name={name} trades={t} /> : <span>{name}</span>}
          {note ? <span style={{ color: "#8c8c8c", fontSize: 11, whiteSpace: "nowrap" }}>- {note}</span> : null}
        </span>
      ),
    };
  });
}

/**
 * WHOSE PEOPLE IS IT SITTING WITH - the second filter. Deliberately not the
 * contractor one: that narrows by whose hours are on an item, this by who the
 * item is assigned to, and the two differ whenever one company's people carry
 * work another company bills for.
 */
export function PartnerTeamSelect({ value, onChange, options, disabled, style }) {
  return (
    <Tooltip title={disabled
      ? "Pick a project first - a partner's team is its people on that project."
      : "Issues and shop drawings ASSIGNED to anyone on that company, whoever logged the hours. Not the same as the Contractor filter, which is whose hours are on the item."}>
      <Select filterOption={matchOption}
        size="small" allowClear showSearch optionFilterProp="search"
        optionLabelProp="text" popupMatchSelectWidth={false}
        style={style || { minWidth: 200 }}
        aria-label="Partner team"
        placeholder={disabled ? "Partner team (pick a project)" : "Partner team"}
        value={value || undefined}
        onChange={(v) => onChange(v || "")}
        options={options}
        disabled={disabled}
        notFoundContent="No partners on this project yet"
      />
    </Tooltip>
  );
}
